import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import { InlineError } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SectionCard } from '@/components/ui/section-card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ResponsiveRows,
  StackedRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  numericCell,
  numericHead,
} from '@/components/ui/table'
import { apiClient, scopedApiPath } from '@/lib/api-client'
import { formatBytesParts, formatDateTime } from '@/lib/format'
import type {
  TranscriptCatalogEntry,
  TranscriptCatalogResponse,
} from '@/lib/types'
import { cn } from '@/lib/utils'
import { useViewAsStore } from '@/stores/view-as-store'

export const Route = createFileRoute('/admin/transcripts')({
  component: AdminTranscriptsPage,
})

function recordingDownloadPath(path: string): string {
  const encoded = path.split('/').map(encodeURIComponent).join('/')
  return `/recording/${encoded}?download=1`
}

function AdminTranscriptsPage() {
  const viewAsId = useViewAsStore((state) => state.viewAsId)
  const [search, setSearch] = useState('')

  const query = useQuery({
    queryKey: ['admin', 'transcripts', viewAsId] as const,
    queryFn: () =>
      apiClient.get<TranscriptCatalogResponse>(
        scopedApiPath('/transcript/?list=1', viewAsId),
      ),
    select: (data): TranscriptCatalogEntry[] => data.transcripts,
  })

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (needle === '') return query.data ?? []
    return (query.data ?? []).filter((entry) =>
      [entry.stream_name, entry.session, entry.recording_name ?? ''].some(
        (value) => value.toLowerCase().includes(needle),
      ),
    )
  }, [query.data, search])

  return (
    <SectionCard
      title="Transcripts"
      description="Every transcript written for your viewing portals, newest first."
    >
      <div className="space-y-4">
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filter by stream key, session or recording"
          aria-label="Filter transcripts"
          className="max-w-md"
        />

        {query.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : query.isError ? (
          <InlineError message="Couldn't load transcripts. Try refreshing." />
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground">
            {search.trim() === ''
              ? 'No transcripts yet. A transcript is written while a stream runs with Transcription turned on.'
              : 'No transcripts match that filter.'}
          </div>
        ) : (
          <ResponsiveRows
            table={
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-full">Stream key</TableHead>
                    <TableHead className={numericHead}>Started</TableHead>
                    <TableHead>Session</TableHead>
                    <TableHead className={numericHead}>Size</TableHead>
                    <TableHead>Recording</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((entry) => (
                    <TranscriptRow key={`${entry.stream_id}:${entry.session}`} entry={entry} />
                  ))}
                </TableBody>
              </Table>
            }
            stacked={
              <StackedRows
                rows={rows}
                rowKey={(e) => `${e.stream_id}:${e.session}`}
                rowProps={() => ({ 'data-testid': 'admin-transcripts-row' })}
                primary={(e) => (
                  <>
                    <div className="truncate">{e.stream_name}</div>
                    <div className="font-mono text-xs font-normal text-muted-foreground">/live/{e.watch_key}</div>
                  </>
                )}
                secondary={(e) => <SizeValue bytes={e.bytes} />}
                fields={[
                  { label: 'Started', value: (e) => formatDateTime(e.started_at, { withZone: true }), numeric: true },
                  { label: 'Session', value: (e) => <span className="font-mono">{e.session}</span> },
                  { label: 'Recording', value: (e) => e.recording_name ?? '—', show: (e) => e.recording_name !== null },
                ]}
                actions={(e) => <TranscriptActions entry={e} />}
              />
            }
          />
        )}
      </div>
    </SectionCard>
  )
}

function SizeValue({ bytes }: { bytes: number }) {
  const { value, unit } = formatBytesParts(bytes)
  return (
    <span className="whitespace-nowrap font-mono tabular-nums">
      {value}
      {unit ? <span className="ml-1 text-xs text-muted-foreground">{unit}</span> : null}
    </span>
  )
}

function TranscriptActions({ entry }: { entry: TranscriptCatalogEntry }) {
  return (
    <>
      {entry.recording_path !== null && (
        <Button asChild variant="outline" size="sm">
          <a href={recordingDownloadPath(entry.recording_path)} download rel="noopener">
            Download recording
          </a>
        </Button>
      )}
      <Button asChild variant="outline" size="sm">
        <a href={entry.portal_path} target="_blank" rel="noopener noreferrer">
          View transcript
        </a>
      </Button>
    </>
  )
}

function TranscriptRow({ entry }: { entry: TranscriptCatalogEntry }) {
  return (
    <TableRow data-testid="admin-transcripts-row">
      <TableCell className="min-w-[12rem]">
        <div className="truncate font-medium text-foreground">{entry.stream_name}</div>
        <div className="font-mono text-xs text-muted-foreground">/live/{entry.watch_key}</div>
      </TableCell>
      <TableCell className={cn(numericCell, 'text-sm text-muted-foreground')}>
        {formatDateTime(entry.started_at, { withZone: true })}
      </TableCell>
      <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
        {entry.session}
      </TableCell>
      <TableCell className={cn(numericCell, 'text-sm')}>
        <SizeValue bytes={entry.bytes} />
      </TableCell>
      <TableCell className="max-w-[14rem] truncate text-sm text-muted-foreground" title={entry.recording_name ?? undefined}>
        {entry.recording_name ?? '—'}
      </TableCell>
      <TableCell className="w-px whitespace-nowrap">
        <div className="flex justify-end gap-2">
          <TranscriptActions entry={entry} />
        </div>
      </TableCell>
    </TableRow>
  )
}
